import { BulkDeleteConfirmDialog } from "@/components/file-upload/bulk-delete-confirm-dialog";
import { DeleteConfirmDialog } from "@/components/file-upload/delete-confirm-dialog";
import { FileList } from "@/components/file-upload/file-list";
import { FileUploadButton } from "@/components/file-upload/upload-button";
import { UploadProgress } from "@/components/file-upload/upload-progress";
import { PageHeader } from "@/components/layout/page-header";
import { LoadingState } from "@/components/ui/loading-state";
import { deleteFile, getFiles, getReconciliationSets } from "@/services/api";
import { useTabActions } from "@/stores/tab-store";
import { useUploadStore } from "@/stores/upload-store";
import type { FileResponse } from "@efes/shared";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2, X } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

/**
 * Страница загрузки файлов — US1 MVP.
 * Объединяет кнопку загрузки, прогресс и таблицу файлов с групповым удалением.
 */
interface UploadPageProps {
	onOpenFileTab?: (file: FileResponse) => void;
}

export function UploadPage({ onOpenFileTab }: UploadPageProps) {
	const queryClient = useQueryClient();
	const { isUploading } = useUploadStore();
	const { closeTabsForSet } = useTabActions();

	/**
	 * Если backend каскадом удалил наборы сверки (последний файл набора удалён),
	 * чистим связанный фронт-state: закрываем табы детали/результата по этим
	 * setId и инвалидируем queries, иначе пользователь увидит "висячий" результат
	 * для уже удалённого набора.
	 */
	const handleCascadeDeletedSets = (setIds: number[]) => {
		if (setIds.length === 0) return;
		for (const setId of setIds) closeTabsForSet(setId);
		queryClient.invalidateQueries({ queryKey: ["reconciliation-sets"] });
		queryClient.invalidateQueries({ queryKey: ["reconciliation-sets-for-bulk-delete"] });
		toast.info(
			setIds.length === 1
				? "Набор сверки автоматически удалён: все его файлы были удалены."
				: `Автоматически удалены ${setIds.length} наборов сверки: все их файлы были удалены.`,
		);
	};
	const [pendingDelete, setPendingDelete] = useState<FileResponse | null>(null);
	const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
	const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
	const [bulkPending, setBulkPending] = useState(false);

	const { data, isLoading } = useQuery({
		queryKey: ["files"],
		queryFn: () => getFiles(),
		refetchInterval: isUploading ? 2000 : false,
	});

	// Загружаем наборы сверки только когда есть выделение — чтобы предупредить
	// о пересечениях в bulk-delete диалоге. На обычной странице запрос не идёт.
	const { data: setsData } = useQuery({
		queryKey: ["reconciliation-sets-for-bulk-delete"],
		queryFn: () => getReconciliationSets({ pageSize: 200 }),
		enabled: selectedIds.size > 0,
		staleTime: 30_000,
	});

	const files = data?.files ?? [];

	const handleDelete = (file: FileResponse) => {
		setPendingDelete(file);
	};

	const handleConfirmDelete = async () => {
		if (!pendingDelete) return;
		try {
			const result = await deleteFile(pendingDelete.id);
			queryClient.invalidateQueries({ queryKey: ["files"] });
			// T-A3.7: одиночное удаление тоже может демотировать набор в 'draft'
			// или (если файл был последним) каскадно удалить набор целиком.
			queryClient.invalidateQueries({ queryKey: ["reconciliation-sets-for-bulk-delete"] });
			handleCascadeDeletedSets(result.cascadeDeletedSetIds);
		} catch (err) {
			const message = err instanceof Error ? err.message : "Не удалось удалить";
			toast.error(message);
		} finally {
			setPendingDelete(null);
		}
	};

	const handleCancelDelete = () => {
		setPendingDelete(null);
	};

	const toggleSelect = (fileId: number) => {
		setSelectedIds((prev) => {
			const next = new Set(prev);
			if (next.has(fileId)) next.delete(fileId);
			else next.add(fileId);
			return next;
		});
	};

	const toggleSelectAll = (allCurrentlySelected: boolean) => {
		setSelectedIds(() => {
			if (allCurrentlySelected) return new Set();
			return new Set(files.map((f) => f.id));
		});
	};

	const clearSelection = () => setSelectedIds(new Set());

	const selectedFiles = files.filter((f) => selectedIds.has(f.id));

	const handleBulkDeleteConfirm = async () => {
		if (selectedFiles.length === 0) return;
		setBulkPending(true);

		// Sequential DELETE — каждый файл атомарно удаляется в backend (cascade
		// в одной транзакции). При ошибке одного — продолжаем остальные и
		// показываем итоговый toast.
		const succeeded: number[] = [];
		const failed: Array<{ name: string; error: string }> = [];
		const cascadeDeletedSetIds = new Set<number>();
		for (const file of selectedFiles) {
			try {
				const result = await deleteFile(file.id);
				succeeded.push(file.id);
				for (const setId of result.cascadeDeletedSetIds) cascadeDeletedSetIds.add(setId);
			} catch (err) {
				const message = err instanceof Error ? err.message : "Неизвестная ошибка";
				failed.push({ name: file.originalName, error: message });
			}
		}

		setBulkPending(false);
		setBulkConfirmOpen(false);
		setSelectedIds(new Set());
		queryClient.invalidateQueries({ queryKey: ["files"] });
		// T-A3.7: после удаления файлов набор может потерять минимум (≥1 wo + ≥1 act)
		// и его status демотируется в 'draft', либо (если файлы были последними) набор
		// каскадом удаляется backend'ом. Сбрасываем кеш + закрываем табы по удалённым
		// наборам.
		queryClient.invalidateQueries({ queryKey: ["reconciliation-sets-for-bulk-delete"] });
		handleCascadeDeletedSets(Array.from(cascadeDeletedSetIds));

		if (failed.length === 0) {
			toast.success(`Удалено файлов: ${succeeded.length}`);
		} else if (succeeded.length === 0) {
			toast.error(`Не удалось удалить ни одного файла (${failed.length} ошибок)`);
		} else {
			toast.warning(
				`Удалено: ${succeeded.length}, ошибок: ${failed.length}. Первая ошибка: ${failed[0].error}`,
			);
		}
	};

	/** Число ДРУГИХ записей files с тем же originalName (одна Excel-книга → один физический файл) */
	const siblingCount = pendingDelete
		? files.filter(
				(f) => f.originalName === pendingDelete.originalName && f.id !== pendingDelete.id,
			).length
		: 0;

	return (
		<div className="space-y-6">
			<PageHeader
				title="Файлы"
				subtitle="Загрузка и распознавание документов"
				actions={<FileUploadButton />}
			/>

			<UploadProgress />

			{isLoading ? (
				<LoadingState />
			) : (
				<FileList
					files={files}
					onDelete={handleDelete}
					onOpenFile={onOpenFileTab}
					selectedIds={selectedIds}
					onToggleSelect={toggleSelect}
					onToggleSelectAll={toggleSelectAll}
				/>
			)}

			{selectedIds.size > 0 && (
				<div className="sticky bottom-4 z-30 mx-auto flex max-w-3xl items-center justify-between gap-3 rounded-lg border border-blue-300 bg-white px-4 py-3 shadow-lg">
					<span className="text-sm font-medium text-slate-700">
						Выбрано файлов: <span className="font-bold">{selectedIds.size}</span>
					</span>
					<div className="flex items-center gap-2">
						<button
							type="button"
							onClick={clearSelection}
							className="inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
						>
							<X className="h-3.5 w-3.5" />
							Снять выделение
						</button>
						<button
							type="button"
							onClick={() => setBulkConfirmOpen(true)}
							className="inline-flex items-center gap-1.5 rounded-md bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 transition-colors"
						>
							<Trash2 className="h-3.5 w-3.5" />
							Удалить выбранные
						</button>
					</div>
				</div>
			)}

			{pendingDelete && (
				<DeleteConfirmDialog
					file={pendingDelete}
					siblingCount={siblingCount}
					onConfirm={handleConfirmDelete}
					onCancel={handleCancelDelete}
				/>
			)}

			{bulkConfirmOpen && selectedFiles.length > 0 && (
				<BulkDeleteConfirmDialog
					files={selectedFiles}
					reconciliationSets={setsData?.items ?? []}
					onConfirm={handleBulkDeleteConfirm}
					onCancel={() => !bulkPending && setBulkConfirmOpen(false)}
					isPending={bulkPending}
				/>
			)}
		</div>
	);
}
